'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SxProps, useTheme } from '@mui/material/styles';
import warnOnce from 'packages/toolpad/toolpad-utils/src/warnOnce';

import { AccountProps } from '../Account';
import { WindowContext, NavigationContext } from '../shared/context';
import { Branding, Navigation, NavigationPageItem } from '../AppProvider';

import { MINI_DRAWER_WIDTH } from './shared';
import { DashboardSidebarSubNavigation } from './DashboardSidebarSubNavigation';
import {
  getDrawerSxTransitionMixin,
  getDrawerWidthTransitionMixin,
} from './utils';
import {
  DashboardHeader,
  type DashboardHeaderProps,
  type DashboardHeaderSlotProps,
} from './DashboardHeader';

export interface SidebarFooterProps {
  mini: boolean;
}

export interface DashboardLayoutSlotProps {
  header?: DashboardHeaderProps;
  sidebarFooter?: SidebarFooterProps;
  appTitle?: DashboardHeaderSlotProps['appTitle'];
  toolbarActions?: DashboardHeaderSlotProps['toolbarActions'];
  toolbarAccount?: DashboardHeaderSlotProps['toolbarAccount'];
}

// @TODO: Deprecate `appTitle` and `toolbarActions` slots so that they must be used in DashboardHeader component only. Update docs accordingly.

export interface DashboardLayoutSlots {
  /**
   * The component used for the app title section in the layout header.
   * @default Link
   * @see [DashboardLayout#slots](https://mui.com/toolpad/core/react-dashboard-layout/#slots)
   */
  appTitle?: React.ElementType;
  /**
   * The toolbar actions component used in the layout header.
   * @default ToolbarActions
   * @see [DashboardLayout#slots](https://mui.com/toolpad/core/react-dashboard-layout/#slots)
   */
  toolbarActions?: React.JSXElementConstructor<{}>;
  /**
   * The toolbar account component used in the layout header.
   * @default Account
   * @deprecated Place your custom component on the right in the `toolbarActions` slot instead.
   * @see [DashboardLayout#slots](https://mui.com/toolpad/core/react-dashboard-layout/#slots)
   */
  toolbarAccount?: React.JSXElementConstructor<AccountProps>;
  /**
   * The component used for the layout header.
   * @default DashboardHeader
   */
  header?: React.JSXElementConstructor<DashboardHeaderProps>;
  /**
   * Optional footer component used in the layout sidebar.
   * @default null
   */
  sidebarFooter?: React.JSXElementConstructor<SidebarFooterProps>;
}

export interface DashboardLayoutProps {
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  // @TODO: Remove deprecated prop from v0.16.0 onwards
  /**
   * Navigation definition for the dashboard. [Find out more](https://mui.com/toolpad/core/react-dashboard-layout/#navigation).
   * @default []
   * @deprecated Set the navigation in the [AppProvider](https://mui.com/toolpad/core/react-app-provider/#navigation) instead.
   */
  navigation?: Navigation;
  /**
   * Whether the navigation bar and menu icon should be hidden.
   * @default false
   */
  hideNavigation?: boolean;
  /**
   * The content of the dashboard.
   */
  children: React.ReactNode;
  /**
   * Branding options for the dashboard.
   * @default null
   */
  branding?: Branding | null;
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: DashboardLayoutSlots;
  /**
   * Whether the sidebar should start collapsed in desktop size screens.
   * @default false
   */
  defaultSidebarCollapsed?: boolean;
  /**
   * Whether the sidebar should not be collapsible to a mini variant in desktop and tablet viewports.
   * @default false
   */
  disableCollapsibleSidebar?: boolean;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: DashboardLayoutSlotProps;
  /**
   * Width of the sidebar when expanded.
   * @default 320
   */
  sidebarExpandedWidth?: number | string;
  /**
   * Render each page item.
   *
   * @param {NavigationPageItem} item
   * @param {{ mini: boolean }} params
   * @returns {ReactNode}
   */
  renderPageItem?: (
    item: NavigationPageItem,
    params: { mini: boolean }
  ) => React.ReactNode;
}

/**
 *
 * Demos:
 *
 * - [Dashboard Layout](https://mui.com/toolpad/core/react-dashboard-layout/)
 *
 * API:
 *
 * - [DashboardLayout API](https://mui.com/toolpad/core/api/dashboard-layout)
 */
function DashboardLayout(props: DashboardLayoutProps) {
  const {
    sx,
    slots,
    children,
    branding,
    slotProps,
    renderPageItem,
    hideNavigation = false,
    navigation: navigationProp,
    sidebarExpandedWidth = 320,
    defaultSidebarCollapsed = false,
    disableCollapsibleSidebar = false,
  } = props;

  if (navigationProp && import.meta.env.NODE_ENV !== 'production') {
    warnOnce(
      'The navigation prop in the DashboardLayout component is deprecated and will eventually be removed. Set the navigation prop in the AppProvider instead (https://mui.com/toolpad/core/react-app-provider/#navigation).'
    );
  }

  const theme = useTheme();

  const navigationContext = React.useContext(NavigationContext);
  const appWindowContext = React.useContext(WindowContext);

  const navigation = navigationProp ?? navigationContext;

  const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] =
    React.useState(!defaultSidebarCollapsed);
  const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] =
    React.useState(false);

  const isOverSmViewport = useMediaQuery(
    theme.breakpoints.up('sm'),
    appWindowContext && {
      matchMedia: appWindowContext.matchMedia,
    }
  );
  const isOverMdViewport = useMediaQuery(
    theme.breakpoints.up('md'),
    appWindowContext && {
      matchMedia: appWindowContext.matchMedia,
    }
  );

  const isNavigationExpanded = isOverMdViewport
    ? isDesktopNavigationExpanded
    : isMobileNavigationExpanded;

  const setIsNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => {
      if (isOverMdViewport) {
        setIsDesktopNavigationExpanded(newExpanded);
      } else {
        setIsMobileNavigationExpanded(newExpanded);
      }
    },
    [isOverMdViewport]
  );

  const [isNavigationFullyExpanded, setIsNavigationFullyExpanded] =
    React.useState(isNavigationExpanded);
  const [isNavigationFullyCollapsed, setIsNavigationFullyCollapsed] =
    React.useState(!isNavigationExpanded);

  React.useEffect(() => {
    if (isNavigationExpanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsNavigationFullyExpanded(true);
      }, theme.transitions.duration.enteringScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    setIsNavigationFullyExpanded(false);

    return () => {};
  }, [isNavigationExpanded, theme]);

  React.useEffect(() => {
    if (!isNavigationExpanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsNavigationFullyCollapsed(true);
      }, theme.transitions.duration.leavingScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    setIsNavigationFullyCollapsed(false);

    return () => {};
  }, [isNavigationExpanded, theme]);

  const handleSetNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => () => {
      setIsNavigationExpanded(newExpanded);
    },
    [setIsNavigationExpanded]
  );

  const handleToggleHeaderMenu = React.useCallback(
    (isExpanded: boolean) => {
      setIsNavigationExpanded(isExpanded);
    },
    [setIsNavigationExpanded]
  );

  const handleNavigationLinkClick = React.useCallback(() => {
    setIsMobileNavigationExpanded(false);
  }, [setIsMobileNavigationExpanded]);

  const isDesktopMini =
    !disableCollapsibleSidebar && !isDesktopNavigationExpanded;
  const isMobileMini =
    !disableCollapsibleSidebar && !isMobileNavigationExpanded;

  const hasDrawerTransitions =
    isOverSmViewport && (!disableCollapsibleSidebar || isOverMdViewport);

  const SidebarFooterSlot = slots?.sidebarFooter ?? null;
  const HeaderSlot = slots?.header ?? DashboardHeader;

  const headerSlotProps: DashboardHeaderProps = React.useMemo(
    () => ({
      branding,
      menuOpen: isNavigationExpanded,
      onToggleMenu: handleToggleHeaderMenu,
      hideMenuButton:
        hideNavigation || (isOverMdViewport && disableCollapsibleSidebar),
      slots: {
        appTitle: slots?.appTitle,
        toolbarActions: slots?.toolbarActions,
        toolbarAccount: slots?.toolbarAccount,
      },
      slotProps: {
        appTitle: slotProps?.appTitle,
        toolbarActions: slotProps?.toolbarActions,
        toolbarAccount: slotProps?.toolbarAccount,
      },
      ...slotProps?.header,
    }),
    [
      branding,
      isNavigationExpanded,
      handleToggleHeaderMenu,
      hideNavigation,
      isOverMdViewport,
      disableCollapsibleSidebar,
      slotProps,
      slots,
    ]
  );

  const getDrawerContent = React.useCallback(
    (isMini: boolean, viewport: 'phone' | 'tablet' | 'desktop') => (
      <React.Fragment>
        <Toolbar />
        <Box
          component="nav"
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: '100%',
            display: 'flex',
            overflow: 'auto',
            overflowX: 'hidden',
            flexDirection: 'column',
            justifyContent: 'space-between',
            scrollbarGutter: isMini ? 'stable' : 'auto',
            pt: navigation[0]?.kind === 'header' && !isMini ? 0 : 2,
            ...(hasDrawerTransitions
              ? getDrawerSxTransitionMixin(isNavigationFullyExpanded, 'padding')
              : {}),
          }}
        >
          <DashboardSidebarSubNavigation
            isMini={isMini}
            subNavigation={navigation}
            renderPageItem={renderPageItem}
            onLinkClick={handleNavigationLinkClick}
            isFullyExpanded={isNavigationFullyExpanded}
            hasDrawerTransitions={hasDrawerTransitions}
            sidebarExpandedWidth={sidebarExpandedWidth}
            isFullyCollapsed={isNavigationFullyCollapsed}
          />
          {SidebarFooterSlot ? (
            <SidebarFooterSlot mini={isMini} {...slotProps?.sidebarFooter} />
          ) : null}
        </Box>
      </React.Fragment>
    ),
    [
      SidebarFooterSlot,
      handleNavigationLinkClick,
      hasDrawerTransitions,
      isNavigationFullyCollapsed,
      isNavigationFullyExpanded,
      navigation,
      sidebarExpandedWidth,
      renderPageItem,
      slotProps?.sidebarFooter,
    ]
  );

  const getDrawerSharedSx = React.useCallback(
    (isMini: boolean, isTemporary: boolean) => {
      const drawerWidth = isMini ? MINI_DRAWER_WIDTH : sidebarExpandedWidth;

      return {
        flexShrink: 0,
        width: drawerWidth,
        displayPrint: 'none',
        ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        ...(isTemporary ? { position: 'absolute' } : {}),
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          position: 'absolute',
          boxSizing: 'border-box',
          backgroundImage: 'none',
          ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        },
      };
    },
    [isNavigationExpanded, sidebarExpandedWidth]
  );

  return (
    <Box
      sx={{
        width: '100vw',
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        ...sx,
      }}
    >
      <HeaderSlot {...headerSlotProps} />
      {!hideNavigation ? (
        <React.Fragment>
          <Drawer
            variant="temporary"
            open={isMobileNavigationExpanded}
            container={appWindowContext?.document.body}
            onClose={handleSetNavigationExpanded(false)}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: {
                md: 'none',
                xs: 'block',
                sm: disableCollapsibleSidebar ? 'block' : 'none',
              },
              ...getDrawerSharedSx(false, true),
            }}
          >
            {getDrawerContent(false, 'phone')}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: {
                xs: 'none',
                md: 'none',
                sm: disableCollapsibleSidebar ? 'none' : 'block',
              },
              ...getDrawerSharedSx(isMobileMini, false),
            }}
          >
            {getDrawerContent(isMobileMini, 'tablet')}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              ...getDrawerSharedSx(isDesktopMini, false),
            }}
          >
            {getDrawerContent(isDesktopMini, 'desktop')}
          </Drawer>
        </React.Fragment>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ displayPrint: 'none' }} />
        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

DashboardLayout.propTypes /* remove-proptypes */ = {
  /**
   * The content of the dashboard.
   */
  children: PropTypes.node,
  /**
   * Whether the navigation bar and menu icon should be hidden.
   * @default false
   */
  hideNavigation: PropTypes.bool,
  /**
   * Render each page item.
   *
   * @param {NavigationPageItem} item
   * @param {{ mini: boolean }} params
   * @returns {ReactNode}
   */
  renderPageItem: PropTypes.func,
  /**
   * Whether the sidebar should start collapsed in desktop size screens.
   * @default false
   */
  defaultSidebarCollapsed: PropTypes.bool,
  /**
   * Whether the sidebar should not be collapsible to a mini variant in desktop and tablet viewports.
   * @default false
   */
  disableCollapsibleSidebar: PropTypes.bool,
  /**
   * Width of the sidebar when expanded.
   * @default 320
   */
  sidebarExpandedWidth: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Branding options for the dashboard.
   * @default null
   */
  branding: PropTypes.shape({
    logo: PropTypes.node,
    title: PropTypes.string,
    homeUrl: PropTypes.string,
  }),
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    header: PropTypes.elementType,
    appTitle: PropTypes.elementType,
    sidebarFooter: PropTypes.elementType,
    toolbarAccount: PropTypes.elementType,
    toolbarActions: PropTypes.elementType,
  }),
  /**
   * Navigation definition for the dashboard. [Find out more](https://mui.com/toolpad/core/react-dashboard-layout/#navigation).
   * @default []
   * @deprecated Set the navigation in the [AppProvider](https://mui.com/toolpad/core/react-app-provider/#navigation) instead.
   */
  navigation: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        icon: PropTypes.node,
        action: PropTypes.node,
        title: PropTypes.string,
        pattern: PropTypes.string,
        segment: PropTypes.string,
        kind: PropTypes.oneOf(['page']),
        children: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.shape({
              title: PropTypes.string.isRequired,
              kind: PropTypes.oneOf(['header']).isRequired,
            }),
            PropTypes.shape({
              kind: PropTypes.oneOf(['divider']).isRequired,
            }),
          ]).isRequired
        ),
      }),
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        kind: PropTypes.oneOf(['header']).isRequired,
      }),
      PropTypes.shape({
        kind: PropTypes.oneOf(['divider']).isRequired,
      }),
    ]).isRequired
  ),
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    toolbarActions: PropTypes.object,
    sidebarFooter: PropTypes.shape({
      mini: PropTypes.bool.isRequired,
    }),
    appTitle: PropTypes.shape({
      branding: PropTypes.shape({
        logo: PropTypes.node,
        title: PropTypes.string,
        homeUrl: PropTypes.string,
      }),
    }),
    toolbarAccount: PropTypes.shape({
      localeText: PropTypes.object,
      slotProps: PropTypes.shape({
        popover: PropTypes.object,
        preview: PropTypes.object,
        signInButton: PropTypes.object,
        signOutButton: PropTypes.object,
        popoverContent: PropTypes.object,
      }),
      slots: PropTypes.shape({
        popover: PropTypes.elementType,
        preview: PropTypes.elementType,
        signInButton: PropTypes.elementType,
        signOutButton: PropTypes.elementType,
        popoverContent: PropTypes.elementType,
      }),
    }),
    header: PropTypes.shape({
      hideMenuButton: PropTypes.bool,
      menuOpen: PropTypes.bool.isRequired,
      onToggleMenu: PropTypes.func.isRequired,
      branding: PropTypes.shape({
        logo: PropTypes.node,
        title: PropTypes.string,
        homeUrl: PropTypes.string,
      }),
      slotProps: PropTypes.shape({
        appTitle: PropTypes.object,
        toolbarAccount: PropTypes.object,
        toolbarActions: PropTypes.object,
      }),
      slots: PropTypes.shape({
        appTitle: PropTypes.elementType,
        toolbarAccount: PropTypes.elementType,
        toolbarActions: PropTypes.elementType,
      }),
    }),
  }),
} as any;

export { DashboardLayout };
